import React, { useState, useEffect } from "react"

function FieldLabel({ title, desc, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-800">
      <span className="block">{title}</span>
      {desc && <span className="block text-xs font-normal text-gray-500">{desc}</span>}
    </label>
  )
}

export default function DocumentaryScreeningStrategyPanel({
  country,
  strategyHitlState,
  resuming,
  onApprove,
  readOnly = false,
}) {
  const [strategy, setStrategy] = useState({
    use_deepweb: true,
    use_pubmed: true,
    use_cochrane: true,
    institutional_domains: "",
    epidemio_max_age_years: "5",
    guideline_max_age_years: "5",
    general_max_age_years: "10",
    min_level1_pct: "50",
    target_sources: "35",
  })

  useEffect(() => {
    if (strategyHitlState?.strategy) {
      const s = strategyHitlState.strategy
      setStrategy({
        use_deepweb: s.use_deepweb ?? true,
        use_pubmed: s.use_pubmed ?? true,
        use_cochrane: s.use_cochrane ?? true,
        institutional_domains: Array.isArray(s.institutional_domains) ? s.institutional_domains.join("\n") : "",
        epidemio_max_age_years: String(s.epidemio_max_age_years ?? 5),
        guideline_max_age_years: String(s.guideline_max_age_years ?? 5),
        general_max_age_years: String(s.general_max_age_years ?? 10),
        min_level1_pct: String(Math.round((s.min_level1_pct ?? 0.5) * 100)),
        target_sources: String(s.target_sources ?? 35),
      })
    }
  }, [strategyHitlState])

  const handleApprove = () => {
    onApprove({
      use_deepweb: strategy.use_deepweb,
      use_pubmed: strategy.use_pubmed,
      use_cochrane: strategy.use_cochrane,
      institutional_domains: strategy.institutional_domains.split("\n").map(s => s.trim()).filter(Boolean),
      epidemio_max_age_years: parseInt(strategy.epidemio_max_age_years, 10) || 5,
      guideline_max_age_years: parseInt(strategy.guideline_max_age_years, 10) || 5,
      general_max_age_years: parseInt(strategy.general_max_age_years, 10) || 10,
      min_level1_pct: (parseInt(strategy.min_level1_pct, 10) || 50) / 100.0,
      target_sources: parseInt(strategy.target_sources, 10) || 35,
    })
  }

  return (
    <div className={`mt-6 border-t border-gray-200 pt-6 space-y-6 transition-opacity ${resuming ? "opacity-50 pointer-events-none" : ""}`}>
      <div className="flex items-center justify-between border-b pb-2">
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
          {readOnly ? "2. Stratégie de Recherche (approuvée)" : "2. Stratégie de Recherche (Éditable)"}
        </h2>
        <span
          className={`px-2.5 py-0.5 rounded text-xs font-semibold ${
            readOnly ? "bg-slate-100 text-slate-700" : "bg-emerald-100 text-emerald-800"
          }`}
        >
          {readOnly ? "En cours d’utilisation pour ce run" : "Générée par l'IA"}
        </span>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400">
        {readOnly
          ? "Paramètres retenus pour la suite du graphe medical_documentalist_screeningv1 (lecture seule)."
          : "Vérifiez et ajustez les paramètres avant de lancer la recherche exhaustive (Phase 1)."}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left column: Sources */}
        <div className="space-y-5 rounded-lg border border-gray-200 bg-gray-50 dark:bg-gray-950 dark:bg-gray-950 p-4">
          <h3 className="font-semibold text-gray-800 text-sm">Bases & Sources</h3>
          
          <div className="space-y-3">
            <label className="flex items-start gap-3">
              <input type="checkbox" disabled={readOnly} className="mt-1 rounded text-indigo-600 focus:ring-indigo-500 disabled:opacity-70" checked={strategy.use_pubmed} onChange={e => setStrategy({...strategy, use_pubmed: e.target.checked})} />
              <div>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">PubMed / E-Utilities</span>
                <span className="block text-xs text-gray-500">Recherche programmatique via API. Haut niveau de preuve.</span>
              </div>
            </label>
            <label className="flex items-start gap-3">
              <input type="checkbox" readOnly={readOnly} disabled={readOnly} className="mt-1 rounded text-indigo-600 focus:ring-indigo-500 disabled:opacity-70" checked={strategy.use_cochrane} onChange={e => setStrategy({...strategy, use_cochrane: e.target.checked})} />
              <div>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Cochrane Library</span>
                <span className="block text-xs text-gray-500">Recherche via DDGS. Méta-analyses et revues systématiques.</span>
              </div>
            </label>
            <label className="flex items-start gap-3">
              <input type="checkbox" disabled={readOnly} className="mt-1 rounded text-indigo-600 focus:ring-indigo-500 disabled:opacity-70" checked={strategy.use_deepweb} onChange={e => setStrategy({...strategy, use_deepweb: e.target.checked})} />
              <div>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Deep Web Search (Littérature grise)</span>
                <span className="block text-xs text-gray-500">Recherche DuckDuckGo. Large couverture pour guidelines et rapports.</span>
              </div>
            </label>
          </div>

          <div className="pt-2">
            <FieldLabel title="Sites Institutionnels Nationaux (Recherche ciblée)" desc={`Domaines déduits pour : ${country || "France"}`} />
            <textarea readOnly={readOnly} rows={4} className="mt-2 block w-full rounded-md border border-gray-300 shadow-sm sm:text-sm font-mono text-xs read-only:bg-gray-100 dark:bg-gray-800 dark:bg-gray-900 read-only:cursor-default" value={strategy.institutional_domains} onChange={e => setStrategy({...strategy, institutional_domains: e.target.value})} placeholder="Un domaine par ligne (ex: has-sante.fr)" />
          </div>
        </div>

        {/* Right column: Filters & Targets */}
        <div className="space-y-5 rounded-lg border border-gray-200 bg-gray-50 dark:bg-gray-950 dark:bg-gray-950 p-4">
          <h3 className="font-semibold text-gray-800 text-sm">Filtres & Objectifs</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Âge max. Épidémiologie</label>
              <div className="flex items-center gap-2">
                <input type="number" readOnly={readOnly} min="1" max="20" className="w-16 rounded-md border-gray-300 sm:text-sm read-only:bg-gray-100 dark:bg-gray-800 dark:bg-gray-900" value={strategy.epidemio_max_age_years} onChange={e => setStrategy({...strategy, epidemio_max_age_years: e.target.value})} />
                <span className="text-sm text-gray-500">ans</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Âge max. Recommandations</label>
              <div className="flex items-center gap-2">
                <input type="number" readOnly={readOnly} min="1" max="20" className="w-16 rounded-md border-gray-300 sm:text-sm read-only:bg-gray-100 dark:bg-gray-800 dark:bg-gray-900" value={strategy.guideline_max_age_years} onChange={e => setStrategy({...strategy, guideline_max_age_years: e.target.value})} />
                <span className="text-sm text-gray-500">ans</span>
              </div>
            </div>
            <div className="flex items-center justify-between border-b border-gray-200 pb-3">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Âge max. Littérature générale</label>
              <div className="flex items-center gap-2">
                <input type="number" readOnly={readOnly} min="1" max="50" className="w-16 rounded-md border-gray-300 sm:text-sm read-only:bg-gray-100 dark:bg-gray-800 dark:bg-gray-900" value={strategy.general_max_age_years} onChange={e => setStrategy({...strategy, general_max_age_years: e.target.value})} />
                <span className="text-sm text-gray-500">ans</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Cible de Niveau 1-2</label>
                <p className="text-xs text-gray-500">Méta-analyses, guidelines, RCT</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">≥</span>
                <input type="number" readOnly={readOnly} min="10" max="100" step="10" className="w-16 rounded-md border-gray-300 sm:text-sm read-only:bg-gray-100 dark:bg-gray-800 dark:bg-gray-900" value={strategy.min_level1_pct} onChange={e => setStrategy({...strategy, min_level1_pct: e.target.value})} />
                <span className="text-sm text-gray-500">%</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Volume Cible (Sources)</label>
                <p className="text-xs text-gray-500">Détermine les boucles de recherche</p>
              </div>
              <input type="number" readOnly={readOnly} min="10" max="100" className="w-20 rounded-md border-gray-300 sm:text-sm font-medium read-only:bg-gray-100 dark:bg-gray-800 dark:bg-gray-900" value={strategy.target_sources} onChange={e => setStrategy({...strategy, target_sources: e.target.value})} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        {readOnly ? (
          <div className="flex w-full items-center justify-end gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900">
            <svg className="h-5 w-5 shrink-0 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Stratégie approuvée — le run continue avec ces paramètres.
          </div>
        ) : (
          <button disabled={resuming} onClick={handleApprove} className="bg-teal-600 text-white px-5 py-2 rounded-md font-bold text-sm hover:bg-teal-700 shadow-sm flex items-center gap-2 disabled:opacity-50">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            {resuming ? "Lancement en cours..." : "Lancer la recherche (Phase 1)"}
          </button>
        )}
      </div>
    </div>
  )
}
